import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast, QtyControl, COLOR_MAP } from "../components/Navbar";
import { useCart } from "../context/CartContext";
import { Helmet } from "react-helmet-async";
import useSEO from "../hooks/useSEO";

/* ═══════════════════════════════════════════════════════════════
   FILTER CONFIG
═══════════════════════════════════════════════════════════════ */
// REPLACE with:
const FILTER_CATEGORIES = [
  {
    label: "Indian Traditional Dresses",
    key: "indian_traditional",
    subcategories: [
      "All Indian Traditional Dresses",
      "Blouse",
      "Chudidar Set",
      "Kurtas",
      "Lehangas",
      "Sarees",
      "Sherwanis",
    ],
  },
  {
    label: "Jewelry",
    key: "jewelry",
    subcategories: [
      "All Jewelry",
      "Anklets",
      "Bangles",
      "Chains",
      "Earrings",
      "Necklace Set",
      "Rings",
      "Tikka",          
      "Waist Belt",    
      "Hathphool",
    ],
  },
  {
    label: "Summer Wear",
    key: "summer_wear",
    subcategories: [
      "All Summer Wear",
      "White Long Frocks",
      "Caps",
    ],
  },
  {
  label: "Winter Wear",
  key: "winter_wear",
  subcategories: [
    "All Winter Wear",          
    "Abaya",
    "Incense Sticks",
    "Indie Pants",
    "Indie Skirts",
    "Scarfs and Pashmina",
    "Pashmina",                // ← new
    "Cap",                     // ← new
    "Hand Gloves",             // ← new
    "Socks",                   // ← new
  ],
 },

  {
    label: "Miscellaneous",
    key: "miscellaneous",
    subcategories: [
      "All Miscellaneous",
    ],
  },
  // {
  //   label: "Cosmetics",
  //   key: "cosmetics",
  //   subcategories: [
  //     "All Cosmetics",
  //     "Lipstick",
  //     "Foundation",
  //     "Skincare",
  //     "Perfumes",
  //     "Nail Care",
  //     "Eye Makeup",
  //   ],
  // },
];

const SIZES = ["XS", "S", "M", "L", "XL", "XXL", "Free Size"];

const COLORS = [
  { name: "Black",  hex: "#1a1a1a" },
  { name: "White",  hex: "#f5f5f4" },
  { name: "Red",    hex: "#e11d48" },
  { name: "Blue",   hex: "#2563eb" },
  { name: "Green",  hex: "#16a34a" },
  { name: "Yellow", hex: "#eab308" },
  { name: "Pink",   hex: "#ec4899" },
  { name: "Purple", hex: "#9333ea" },
  { name: "Orange", hex: "#ea580c" },
  { name: "Gold",   hex: "#d97706" },
];

const PRICE_RANGES = [
  { label: "Under $25",    min: 0,   max: 25  },
  { label: "$25 – $50",   min: 25,  max: 50  },
  { label: "$50 – $100",  min: 50,  max: 100 },
  { label: "$100 – $200", min: 100, max: 200 },
  { label: "Over $200",   min: 200, max: Infinity },
];

/* ═══════════════════════════════════════════════════════════════
   STARS
═══════════════════════════════════════════════════════════════ */
function Stars({ rating, size = "sm" }) {
  const sz = size === "sm" ? "w-3 h-3" : "w-4 h-4";
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <svg key={n} className={`${sz} ${n <= Math.round(rating) ? "fill-amber-400" : "fill-stone-200"}`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      ))}
      <span className="text-[11px] text-stone-400 ml-1 font-medium">{rating}</span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   PRODUCT CARD
═══════════════════════════════════════════════════════════════ */
const TAG_STYLES = {
  Premium:    "bg-amber-100 text-amber-700",
  Bestseller: "bg-rose-100 text-rose-700",
  Limited:    "bg-purple-100 text-purple-700",
  New:        "bg-emerald-100 text-emerald-700",
  Trending:   "bg-sky-100 text-sky-700",
};

function ProductCard({ product, index }) {
  const { addToCart, isInCart, getQuantity, updateQuantity } = useCart();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const [wishlisted, setWishlisted] = useState(false);
  const [btnKey, setBtnKey] = useState(0);

  const inCart = isInCart(product.id);
  const qty    = getQuantity(product.id);

  const imgSrc = Array.isArray(product.images) && product.images.length > 0
    ? product.images[0]
    : product.img || "";

  const colorList = Array.isArray(product.colors) ? product.colors : [];
  const MAX_COLOR_DOTS = 5;
  const visibleColors = colorList.slice(0, MAX_COLOR_DOTS);
  const extraColorCount = colorList.length - visibleColors.length;

  const displayPrice = product.sale_price || product.price;

  const handleAdd = (e) => {
    e.stopPropagation();
    addToCart(product.id, 1);
    addToast(`${product.name} added to cart`);
    setBtnKey((k) => k + 1);
  };

  return (
    <div
      onClick={() => {
  sessionStorage.setItem('shopScrollY', window.scrollY);
  navigate(`/product/${product.id}`);
}}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 hover:-translate-y-1.5 border border-stone-100 flex flex-col cursor-pointer"
      style={{ animationDelay: `${(index % 8) * 45}ms` }}
    >
      <div className="relative aspect-[3/4] overflow-hidden bg-stone-100">
        <img src={imgSrc} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 bg-stone-900/0 group-hover:bg-stone-900/8 transition-colors duration-300" />
        {product.tag && (
          <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${TAG_STYLES[product.tag] ?? "bg-stone-100 text-stone-600"}`}>
            {product.tag}
          </span>
        )}
        <button
          onClick={(e) => { e.stopPropagation(); setWishlisted((w) => !w); }}
          className={`absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all duration-300 ${wishlisted ? "bg-rose-500 text-white scale-110" : "bg-white/90 text-stone-400 opacity-0 group-hover:opacity-100 hover:text-rose-500"}`}
        >
          <svg className="w-4 h-4" fill={wishlisted ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
        </button>
        {inCart && (
          <div className="absolute bottom-3 left-3 right-3" onClick={e => e.stopPropagation()}>
            <div className="bg-stone-900/85 backdrop-blur-sm rounded-xl px-3 py-1.5 flex items-center justify-between">
              <span className="text-white text-xs font-semibold">In cart</span>
              <QtyControl variant="dark" value={qty} onChange={(newQty) => updateQuantity(product.id, newQty)} />
            </div>
          </div>
        )}
      </div>

      <div className="p-4 flex flex-col flex-1">
        <span className="text-[10px] font-semibold tracking-widest uppercase text-stone-400 mb-1">{product.category}</span>
        <h3 className="serif text-base font-semibold text-stone-900 mb-1.5 leading-snug">{product.name}</h3>
        <Stars rating={product.rating} />
        <div className="flex items-center gap-2 mt-2 mb-3">
          {visibleColors.length > 0 && (
            <div className="flex items-center -space-x-1">
              {visibleColors.map((c, i) => (
                <span
                  key={c.name ?? i}
                  title={c.name}
                  className="w-3.5 h-3.5 rounded-full border-2 border-white ring-1 ring-stone-200 flex-shrink-0"
                  style={{ backgroundColor: c.hex ?? COLOR_MAP[c.name] ?? "#ccc" }}
                />
              ))}
              {extraColorCount > 0 && (
                <span className="text-[9px] font-semibold text-stone-400 pl-2">+{extraColorCount}</span>
              )}
            </div>
          )}
          {Array.isArray(product.sizes) && product.sizes.map((s) => (
            <span key={s} className="text-[10px] font-semibold px-1.5 py-0.5 border border-stone-200 rounded text-stone-500">{s}</span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-auto gap-2">
          <div className="flex items-center gap-1.5 flex-shrink-0 min-w-0">
            <span className="serif text-lg font-bold text-stone-900 truncate">${displayPrice}</span>
            {product.sale_price && product.price > product.sale_price && (
              <span className="text-xs text-stone-400 line-through flex-shrink-0">${product.price}</span>
            )}
          </div>
          <button
            key={btnKey}
            onClick={handleAdd}
            className={`flex items-center justify-center gap-1.5 font-semibold rounded-full transition-all duration-200 active:scale-95 flex-shrink-0 px-2.5 py-2 sm:px-3.5 sm:py-2 text-xs ${inCart ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-stone-900 text-white hover:bg-rose-500"}`}
          >
            {inCart ? (
              <>
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                <span className="hidden sm:inline whitespace-nowrap">Add More</span>
              </>
            ) : (
              <>
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4" /><circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /></svg>
                <span className="hidden sm:inline whitespace-nowrap">Add to Cart</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   FILTER SECTION (accordion item for sidebar)
═══════════════════════════════════════════════════════════════ */
function FilterSection({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-stone-100 pb-4 mb-4 last:border-0 last:mb-0">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center justify-between w-full text-xs font-bold text-stone-800 uppercase tracking-widest mb-3 hover:text-rose-500 transition-colors"
      >
        {title}
        <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   DESKTOP SIDEBAR FILTERS
═══════════════════════════════════════════════════════════════ */
function SidebarFilters({ filters, onChange, onApply, onReset, resultCount }) {
  const [openCats, setOpenCats] = useState({
    indian_traditional: true,
    jewelry: false,
    summer_wear: false,
    winter_wear: false,
    miscellaneous: false,
  });

  const toggleCat = (key) => setOpenCats(o => ({ ...o, [key]: !o[key] }));

  const toggleSubcat = (sub) => {
    const current = filters.subcategories || [];
    onChange({ ...filters, subcategories: current.includes(sub) ? current.filter(s => s !== sub) : [...current, sub] });
  };
  const toggleSize = (size) => {
    const current = filters.sizes || [];
    onChange({ ...filters, sizes: current.includes(size) ? current.filter(s => s !== size) : [...current, size] });
  };
  const toggleColor = (color) => {
    const current = filters.colors || [];
    onChange({ ...filters, colors: current.includes(color) ? current.filter(c => c !== color) : [...current, color] });
  };

  const activeCount =
    (filters.subcategories?.length || 0) +
    (filters.sizes?.length || 0) +
    (filters.colors?.length || 0) +
    (filters.priceRange ? 1 : 0);

  return (
    <aside className="hidden lg:block w-60 flex-shrink-0 self-start sticky top-24">
  <div className="bg-white rounded-2xl border border-stone-100 shadow-sm p-5 max-h-[calc(100vh-7rem)] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-sm font-bold text-stone-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-rose-500" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
            </svg>
            Filters
            {activeCount > 0 && (
              <span className="bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{activeCount}</span>
            )}
          </h2>
          {activeCount > 0 && (
            <button onClick={onReset} className="text-xs text-stone-400 hover:text-rose-500 transition-colors font-medium">Reset</button>
          )}
        </div>

        {/* Category */}
        <FilterSection title="Category">
          <div className="space-y-3">
            {FILTER_CATEGORIES.map(cat => (
              <div key={cat.key}>
                <button
                  onClick={() => toggleCat(cat.key)}
                  className="flex items-center justify-between w-full text-[10px] font-bold uppercase tracking-wider text-stone-500 mb-1.5 hover:text-rose-500 transition-colors"
                >
                  {cat.label}
                  <svg className={`w-3 h-3 transition-transform duration-150 ${openCats[cat.key] ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {openCats[cat.key] && (
                  <div className="pl-2 space-y-1.5">
                    {cat.subcategories.map(sub => (
                      <label key={sub} className="flex items-center gap-2 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={(filters.subcategories || []).includes(sub)}
                          onChange={() => toggleSubcat(sub)}
                          className="w-3.5 h-3.5 rounded border-stone-300 text-rose-500 focus:ring-rose-300 cursor-pointer accent-rose-500"
                        />
                        <span className="text-xs text-stone-500 group-hover:text-stone-900 transition-colors leading-none">{sub}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </FilterSection>

        {/* Size */}
        <FilterSection title="Size" defaultOpen={false}>
          <div className="flex flex-wrap gap-1.5">
            {SIZES.map(size => (
              <button
                key={size}
                onClick={() => toggleSize(size)}
                className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all duration-150 ${
                  (filters.sizes || []).includes(size)
                    ? "bg-stone-900 text-white border-stone-900"
                    : "border-stone-200 text-stone-600 hover:border-stone-400 bg-white"
                }`}
              >
                {size}
              </button>
            ))}
          </div>
        </FilterSection>

        {/* Color */}
        <FilterSection title="Color" defaultOpen={false}>
          <div className="flex flex-wrap gap-2">
            {COLORS.map(color => (
              <button
                key={color.name}
                title={color.name}
                onClick={() => toggleColor(color.name)}
                className={`w-6 h-6 rounded-full border-2 transition-all duration-150 hover:scale-110 ${
                  (filters.colors || []).includes(color.name)
                    ? "border-stone-900 scale-110 shadow-md"
                    : "border-stone-200 hover:border-stone-400"
                }`}
                style={{ backgroundColor: color.hex }}
              />
            ))}
          </div>
          {(filters.colors || []).length > 0 && (
            <p className="text-[10px] text-stone-400 mt-2">{filters.colors.join(", ")}</p>
          )}
        </FilterSection>

        {/* Price */}
        <FilterSection title="Price" defaultOpen={false}>
          <div className="space-y-2">
            {PRICE_RANGES.map(range => (
              <label key={range.label} className="flex items-center gap-2 cursor-pointer group">
                <input
                  type="radio"
                  name="price-desktop"
                  checked={filters.priceRange?.label === range.label}
                  onChange={() => onChange({ ...filters, priceRange: range })}
                  className="w-3.5 h-3.5 text-rose-500 border-stone-300 focus:ring-rose-300 cursor-pointer accent-rose-500"
                />
                <span className="text-xs text-stone-500 group-hover:text-stone-900 transition-colors">{range.label}</span>
              </label>
            ))}
            {filters.priceRange && (
              <button onClick={() => onChange({ ...filters, priceRange: null })} className="text-xs text-rose-400 hover:text-rose-600 mt-1 transition-colors">Clear price</button>
            )}
          </div>
        </FilterSection>

        {/* Apply button */}
        <button
          onClick={onApply}
          className="w-full bg-stone-900 text-white py-2.5 rounded-full text-sm font-semibold hover:bg-rose-500 transition-all duration-300 hover:shadow-lg hover:shadow-rose-200 mt-1"
        >
          Apply Filters
          {resultCount !== null && (
            <span className="ml-1.5 text-white/50 text-xs">({resultCount})</span>
          )}
        </button>
      </div>
    </aside>
  );
}

/* ═══════════════════════════════════════════════════════════════
   MOBILE FILTER BAR
═══════════════════════════════════════════════════════════════ */
function MobileFilterBar({ filters, onChange, onApply, onReset }) {
  const [openDropdown, setOpenDropdown] = useState(null);
  const barRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (barRef.current && !barRef.current.contains(e.target)) setOpenDropdown(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const activeCount =
    (filters.subcategories?.length || 0) +
    (filters.sizes?.length || 0) +
    (filters.colors?.length || 0) +
    (filters.priceRange ? 1 : 0);

  const toggleSubcat = (sub) => {
    const current = filters.subcategories || [];
    onChange({ ...filters, subcategories: current.includes(sub) ? current.filter(s => s !== sub) : [...current, sub] });
  };
  const toggleSize = (size) => {
    const current = filters.sizes || [];
    onChange({ ...filters, sizes: current.includes(size) ? current.filter(s => s !== size) : [...current, size] });
  };
  const toggleColor = (color) => {
    const current = filters.colors || [];
    onChange({ ...filters, colors: current.includes(color) ? current.filter(c => c !== color) : [...current, color] });
  };

  const FilterPill = ({ id, label, activeCount: count }) => (
    <button
      onClick={() => setOpenDropdown(d => d === id ? null : id)}
      className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold border transition-all duration-150 flex-shrink-0 ${
        openDropdown === id
          ? "bg-stone-900 text-white border-stone-900"
          : count > 0
          ? "bg-rose-50 text-rose-700 border-rose-200"
          : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"
      }`}
    >
      {label}
      {count > 0 && openDropdown !== id && (
        <span className="bg-rose-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">{count}</span>
      )}
      <svg className={`w-3 h-3 transition-transform ${openDropdown === id ? "rotate-180" : ""}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  );

  return (
    <div ref={barRef} className="lg:hidden relative mb-5">
      {/* Pill bar */}
      <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {/* Filter icon */}
        <div className="flex items-center gap-1 flex-shrink-0 pr-1 border-r border-stone-200">
          <svg className="w-4 h-4 text-stone-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
          </svg>
          {activeCount > 0 && (
            <span className="bg-rose-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{activeCount}</span>
          )}
        </div>

        <FilterPill id="Category" label="Category" activeCount={filters.subcategories?.length || 0} />
        <FilterPill id="Size"     label="Size"     activeCount={filters.sizes?.length || 0} />
        <FilterPill id="Color"    label="Color"    activeCount={filters.colors?.length || 0} />
        <FilterPill id="Price"    label="Price"    activeCount={filters.priceRange ? 1 : 0} />

        {activeCount > 0 && (
          <>
            <button
              onClick={() => { onApply(); setOpenDropdown(null); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-bold bg-rose-500 text-white border border-rose-500 hover:bg-rose-600 transition-colors"
            >
              Apply
            </button>
            <button
              onClick={() => { onReset(); setOpenDropdown(null); }}
              className="flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold bg-white border border-stone-200 text-stone-500 hover:border-stone-400 transition-colors"
            >
              Reset
            </button>
          </>
        )}
      </div>

      {/* Dropdown panel */}
      {openDropdown && (
        <div className="absolute top-full left-0 right-0 z-30 bg-white rounded-2xl shadow-2xl border border-stone-100 p-4 mt-2 max-h-72 overflow-y-auto">

          {openDropdown === "Category" && (
            <div className="space-y-4">
              {FILTER_CATEGORIES.map(cat => (
                <div key={cat.key}>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 mb-2">{cat.label}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cat.subcategories.map(sub => (
                      <button
                        key={sub}
                        onClick={() => toggleSubcat(sub)}
                        className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-all ${
                          (filters.subcategories || []).includes(sub)
                            ? "bg-stone-900 text-white border-stone-900"
                            : "border-stone-200 text-stone-600 hover:border-stone-400 bg-white"
                        }`}
                      >
                        {sub}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {openDropdown === "Size" && (
            <div className="flex flex-wrap gap-2">
              {SIZES.map(size => (
                <button
                  key={size}
                  onClick={() => toggleSize(size)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    (filters.sizes || []).includes(size)
                      ? "bg-stone-900 text-white border-stone-900"
                      : "border-stone-200 text-stone-600 hover:border-stone-400 bg-white"
                  }`}
                >
                  {size}
                </button>
              ))}
            </div>
          )}

          {openDropdown === "Color" && (
            <div>
              <div className="flex flex-wrap gap-3 mb-2">
                {COLORS.map(color => (
                  <button
                    key={color.name}
                    title={color.name}
                    onClick={() => toggleColor(color.name)}
                    className={`w-8 h-8 rounded-full border-2 transition-all hover:scale-110 ${
                      (filters.colors || []).includes(color.name)
                        ? "border-stone-900 scale-110 shadow-md"
                        : "border-stone-200"
                    }`}
                    style={{ backgroundColor: color.hex }}
                  />
                ))}
              </div>
              {(filters.colors || []).length > 0 && (
                <p className="text-xs text-stone-400">{filters.colors.join(", ")}</p>
              )}
            </div>
          )}

          {openDropdown === "Price" && (
            <div className="space-y-2.5">
              {PRICE_RANGES.map(range => (
                <label key={range.label} className="flex items-center gap-2.5 cursor-pointer group">
                  <input
                    type="radio"
                    name="price-mobile"
                    checked={filters.priceRange?.label === range.label}
                    onChange={() => onChange({ ...filters, priceRange: range })}
                    className="w-4 h-4 text-rose-500 border-stone-300 focus:ring-rose-300 accent-rose-500 cursor-pointer"
                  />
                  <span className="text-sm text-stone-600 group-hover:text-stone-900 transition-colors">{range.label}</span>
                </label>
              ))}
              {filters.priceRange && (
                <button onClick={() => onChange({ ...filters, priceRange: null })} className="text-xs text-rose-400 hover:text-rose-600 mt-1 transition-colors">Clear</button>
              )}
            </div>
          )}

          <button
            onClick={() => { onApply(); setOpenDropdown(null); }}
            className="w-full mt-4 bg-stone-900 text-white py-2.5 rounded-full text-sm font-semibold hover:bg-rose-500 transition-colors"
          >
            Apply Filters
          </button>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HERO STRIP
═══════════════════════════════════════════════════════════════ */
function ShopHero() {
  const { totalItems, totalPrice } = useCart();
  return (
    <div className="relative overflow-hidden bg-stone-900 pt-20">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1558769132-cb1aea458c5e?w=1600&q=55')", backgroundSize: "cover", backgroundPosition: "center" }} />
      <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 to-stone-900/55" />
      <div className="relative max-w-screen-xl mx-auto px-6 py-14 flex flex-col md:flex-row items-start md:items-end justify-between gap-6">
        <div>
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-rose-400 mb-3 block">Premium Collection</span>
          <h1 className="serif text-5xl md:text-6xl font-bold text-white leading-tight">
            Sagar Fashion<br />
            <em className="not-italic text-transparent bg-clip-text bg-gradient-to-r from-rose-300 to-amber-300">Shop &amp; Cart</em>
          </h1>
        </div>
        {totalItems > 0 && (
          <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 text-right">
            <div className="text-xs text-white/50 uppercase tracking-wider mb-1">Cart Summary</div>
            <div className="serif text-3xl font-bold text-white">${totalPrice}</div>
            <div className="text-white/60 text-sm">{totalItems} item{totalItems !== 1 ? "s" : ""} selected</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   SHOP PAGE
═══════════════════════════════════════════════════════════════ */
export default function ShopPage() {
  const [searchParams] = useSearchParams();
const [products,         setProducts]        = useState([]);
const [filteredProducts, setFilteredProducts] = useState([]);
const [loading,          setLoading]          = useState(true);
const [pendingFilters,   setPendingFilters]   = useState({ subcategories: [], sizes: [], colors: [], priceRange: null });
const [appliedFilters,   setAppliedFilters]   = useState({ subcategories: [], sizes: [], colors: [], priceRange: null });
const { totalItems } = useCart();
const seo = useSEO("shop");

// Fetch all products once
useEffect(() => {
  setLoading(true);
  const API = process.env.REACT_APP_API_URL || "http://localhost:5001";
  fetch(`${API}/api/products`)
  .then(r => r.json())
  .then(data => {
  const list = Array.isArray(data) ? data : [];
  setProducts(list);

  const categoryParam = searchParams.get("category");
  if (categoryParam) {
    const filtered = list.filter(p =>
      (p.category || "").toLowerCase() === categoryParam.toLowerCase()
    );
    setFilteredProducts(filtered);
    setAppliedFilters(f => ({ ...f, subcategories: [`All ${categoryParam}`] }));
    setPendingFilters(f => ({ ...f, subcategories: [`All ${categoryParam}`] }));
  } else {
    setFilteredProducts(list);
  }

  setLoading(false);
        const savedY = sessionStorage.getItem('shopScrollY');
        if (savedY) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              window.scrollTo({ top: parseInt(savedY), behavior: 'instant' });
              sessionStorage.removeItem('shopScrollY');
            });
          });
        }
      })
      .catch(() => setLoading(false));
}, [searchParams]);

  const applyFilters = (filtersToApply = pendingFilters) => {
  setAppliedFilters(filtersToApply);
  let result = [...products];
  console.log("Applying filters:", filtersToApply.subcategories);
  console.log("All products categories:", [...new Set(products.map(p => p.category))]);
  // console.log("Cosmetics products:", products.filter(p => p.category === "Cosmetics"));

    if (filtersToApply.subcategories?.length > 0) {
  // Map each selected subcategory/group to the DB category value
  const DB_CATEGORY_MAP = {
    "All Indian Traditional Dresses": "Indian Traditional Dresses",
    "All Miscellaneous": "Miscellaneous",
    "Blouse":            "Indian Traditional Dresses",
    "Chudidar Set":      "Indian Traditional Dresses",
    "Kurtas":            "Indian Traditional Dresses",
    "Lehangas":          "Indian Traditional Dresses",
    "Sarees":            "Indian Traditional Dresses",
    "Sherwanis":         "Indian Traditional Dresses",
    "All Jewelry":       "Jewelry",
    "Anklets":           "Jewelry",
    "Bangles":           "Jewelry",
    "Chains":            "Jewelry",
    "Earrings":          "Jewelry",
    "Necklace Set":      "Jewelry",
    "Rings":             "Jewelry",
    "Tikka":             "Jewelry",   
    "Waist Belt":        "Jewelry",   
    "Hathphool":         "Jewelry",   
    "All Summer Wear":   "Summer Wear",
    "White Long Frocks": "Summer Wear",
    "Caps":              "Summer Wear",
    "Abaya":             "Winter Wear",
    "Incense Sticks":    "Winter Wear",
    "Indie Pants":       "Winter Wear",
    "Indie Skirts":      "Winter Wear",
    "Scarfs and Pashmina": "Winter Wear",
    "Pashmina":      "Winter Wear",
    "Cap":           "Winter Wear",
    "Hand Gloves":   "Winter Wear",
    "Socks":         "Winter Wear",
  };

  // Collect all DB categories that match the selected filters
  const matchingDbCategories = [
    ...new Set(
      filtersToApply.subcategories
        .map(sub => DB_CATEGORY_MAP[sub])
        .filter(Boolean)
    )
  ];

  if (matchingDbCategories.length > 0) {
    result = result.filter(p => {
      if (!matchingDbCategories.includes(p.category)) return false;

      // For subcategory-specific filters (not "All X"), also check subcategory
      const specificSubs = filtersToApply.subcategories.filter(
        s => !s.startsWith("All ")
      );
      if (specificSubs.length === 0) return true;

      // If this product's category matches, check if subcategory also matches
      const catSpecificSubs = specificSubs.filter(s => DB_CATEGORY_MAP[s] === p.category);
      if (catSpecificSubs.length === 0) return true;

      return catSpecificSubs.includes(p.subcategory);
    });
  }
}

    if (filtersToApply.sizes?.length > 0) {
      result = result.filter(p =>
        Array.isArray(p.sizes) && p.sizes.some(s => filtersToApply.sizes.includes(s))
      );
    }

    if (filtersToApply.colors?.length > 0) {
      result = result.filter(p =>
        Array.isArray(p.colors) && p.colors.some(c =>
          filtersToApply.colors.includes(c.name || c)
        )
      );
    }

    if (filtersToApply.priceRange) {
      const { min, max } = filtersToApply.priceRange;
      result = result.filter(p => {
        const price = p.sale_price || p.price || 0;
        return price >= min && price <= max;
      });
    }

    setFilteredProducts(result);
  };

  const resetFilters = () => {
    const empty = { subcategories: [], sizes: [], colors: [], priceRange: null };
    setPendingFilters(empty);
    setAppliedFilters(empty);
    setFilteredProducts(products);
  };

  const isFiltered =
    appliedFilters.subcategories?.length > 0 ||
    appliedFilters.sizes?.length > 0 ||
    appliedFilters.colors?.length > 0 ||
    !!appliedFilters.priceRange;

  return (
    <>
    <Helmet>
  <title>{seo?.title || "Shop — Sagar Fashion"}</title>
  <meta name="description" content={seo?.description || "Browse our complete collection."} />
  <meta name="keywords"    content={seo?.keywords    || "shop, clothing, jewelry, sagar fashion"} />
  <meta property="og:title"       content={seo?.og_title       || seo?.title || "Shop — Sagar Fashion"} />
  <meta property="og:description" content={seo?.og_description || seo?.description || ""} />
</Helmet>
      <ShopHero />

      <main className="max-w-screen-xl mx-auto px-4 md:px-6 py-10">
        <div className="flex gap-8 items-start">

          {/* Desktop sidebar */}
          <SidebarFilters
            filters={pendingFilters}
            onChange={setPendingFilters}
            onApply={() => applyFilters()}
            onReset={resetFilters}
            resultCount={filteredProducts.length}
          />

          {/* Main content */}
          <div className="flex-1 min-w-0">

            {/* Mobile filter bar */}
            <MobileFilterBar
              filters={pendingFilters}
              onChange={setPendingFilters}
              onApply={() => applyFilters()}
              onReset={resetFilters}
            />

            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-stone-500">
                <span className="font-semibold text-stone-900">{filteredProducts.length}</span> products
                {isFiltered && <span className="text-stone-400 ml-1">(filtered)</span>}
              </p>
              {isFiltered && (
                <button onClick={resetFilters} className="text-xs text-rose-500 hover:text-rose-700 font-medium transition-colors">
                  Clear all filters
                </button>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-10 h-10 border-2 border-stone-200 border-t-rose-400 rounded-full animate-spin" />
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3 text-stone-400">
                <svg className="w-12 h-12 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium">No products match your filters</p>
                <button onClick={resetFilters} className="text-xs text-rose-500 hover:underline font-medium">Clear all filters</button>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {filteredProducts.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Floating cart CTA (mobile) */}
        {totalItems > 0 && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 md:hidden">
            <button
              onClick={() => window.dispatchEvent(new CustomEvent("sf:open-cart"))}
              className="flex items-center gap-3 bg-stone-900 text-white px-6 py-3.5 rounded-full shadow-2xl shadow-stone-900/30 hover:bg-rose-500 transition-all duration-300 font-semibold text-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              View Cart ({totalItems} items)
            </button>
          </div>
        )}
      </main>
    </>
  );
}